import React, { useState, useEffect } from 'react';

const MonthlyCalendar = ({ 
  employee, 
  onClose, 
  onSave,
  employeeAbsences = []
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [monthlySchedule, setMonthlySchedule] = useState(employee.monthlySchedule || {});

  // Resetear estado cuando cambia el empleado
  useEffect(() => {
    setMonthlySchedule(employee.monthlySchedule || {});
    setSelectedDate(null);
    setEditingSchedule(null);
    setRangeMode(false);
    setRangeStart(null);
    setRangeEnd(null);
  }, [employee.id]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [copiedWeek, setCopiedWeek] = useState(null);
  // ── Modo rango ──
  const [rangeMode, setRangeMode] = useState(false);
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  // ── Modo día de semana ──
  const [weekdayMode, setWeekdayMode] = useState(false);
  const [selectedWeekday, setSelectedWeekday] = useState(null); // 0=Lun..6=Dom
  const [weekdaySchedule, setWeekdaySchedule] = useState({ enabled: true, start: '09:00', end: '18:00', lunchStart: '13:00', lunchEnd: '14:00' });

  // Funciones auxiliares
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = (firstDay.getDay() + 6) % 7; // 0=Lun
    
    return { daysInMonth, startDayOfWeek, year, month };
  };

  const getScheduleForDate = (date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
    return monthlySchedule[dateStr] || null;
  };

  const setScheduleForDate = (date, schedule) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
    setMonthlySchedule(prev => ({
      ...prev,
      [dateStr]: schedule
    }));
  };

  const copyWeekSchedule = (startDate) => {
    const schedules = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      const schedule = getScheduleForDate(date);
      if (schedule) schedules.push({ date: new Date(date), schedule });
    }
    return schedules;
  };

  const pasteWeekSchedule = (targetStartDate, copiedSchedules) => {
    copiedSchedules.forEach(({ schedule }, index) => {
      const targetDate = new Date(targetStartDate);
      targetDate.setDate(targetDate.getDate() + index);
      setScheduleForDate(targetDate, schedule);
    });
  };

  const applyWeekday = () => {
    if (selectedWeekday === null) return;
    // selectedWeekday: 0=Lun, 1=Mar, ..., 6=Dom (según dayNames)
    // JS getDay: 0=Dom, 1=Lun, ..., 6=Sáb
    const jsDay = selectedWeekday === 6 ? 0 : selectedWeekday + 1;
    let count = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      if (date.getDay() === jsDay) {
        setScheduleForDate(date, weekdaySchedule.enabled ? {...weekdaySchedule} : null);
        count++;
      }
    }
    const t = document.createElement('div');
    t.textContent = `✅ Horario aplicado a ${count} ${dayNames[selectedWeekday]}(s) — presiona Guardar Todo`;
    t.style.cssText = 'position:fixed;bottom:100px;left:50%;transform:translateX(-50%);background:#22c55e;color:white;padding:10px 20px;border-radius:10px;font-weight:600;font-size:13px;z-index:9999999;font-family:sans-serif;text-align:center;max-width:90vw;';
    document.body.appendChild(t);
    setTimeout(() => t.remove(), 2500);
    setWeekdayMode(false);
    setSelectedWeekday(null);
  };

  const applyRange = () => {
    if (!rangeStart || !rangeEnd) return;
    const start = rangeStart < rangeEnd ? rangeStart : rangeEnd;
    const end = rangeStart < rangeEnd ? rangeEnd : rangeStart;
    const sourceSchedule = getScheduleForDate(start);
    if (!sourceSchedule || !sourceSchedule.enabled) {
      const t = document.createElement('div');
      t.textContent = '⚠️ El día origen no tiene horario configurado';
      t.style.cssText = 'position:fixed;bottom:100px;left:50%;transform:translateX(-50%);background:#f59e0b;color:white;padding:10px 20px;border-radius:10px;font-weight:600;font-size:13px;z-index:9999999;font-family:sans-serif;text-align:center;max-width:90vw;';
      document.body.appendChild(t);
      setTimeout(() => t.remove(), 2500);
      return;
    }
    const current = new Date(start);
    let count = 0;
    while (current <= end) {
      setScheduleForDate(new Date(current), {...sourceSchedule});
      current.setDate(current.getDate() + 1);
      count++;
    }
    const t = document.createElement('div');
    t.textContent = `✅ Horario aplicado a ${count} día(s) — presiona Guardar Todo`;
    t.style.cssText = 'position:fixed;bottom:100px;left:50%;transform:translateX(-50%);background:#22c55e;color:white;padding:10px 20px;border-radius:10px;font-weight:600;font-size:13px;z-index:9999999;font-family:sans-serif;text-align:center;max-width:90vw;';
    document.body.appendChild(t);
    setTimeout(() => t.remove(), 2500);
    setRangeMode(false);
    setRangeStart(null);
    setRangeEnd(null);
  };

  const handleSave = () => {
    // Mostrar toast ANTES de llamar onSave para que sea visible en iOS
    const toast = document.createElement('div');
    toast.textContent = '✅ Horario guardado correctamente';
    toast.style.cssText = 'position:fixed;bottom:30px;left:50%;transform:translateX(-50%);background:#22c55e;color:white;padding:14px 28px;border-radius:12px;font-weight:700;font-size:15px;z-index:9999999;box-shadow:0 4px 20px rgba(0,0,0,0.4);font-family:sans-serif;';
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2500);
    // Llamar onSave — App.js se encarga de cerrar el calendario
    onSave({ ...employee, monthlySchedule });
  };

  const { daysInMonth, startDayOfWeek, year, month } = getDaysInMonth(currentMonth);
  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const dayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  const renderDay = (day) => {
    if (!day) return <div key={Math.random()} style={{ padding: '10px' }}></div>;
    
    const date = new Date(year, month, day);
    const dateStr2 = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
    // Verificar ausencia del empleado para este dia
    const _dayAbsence = employeeAbsences.find(a => a.startDate <= dateStr2 && a.endDate >= dateStr2);
    const isVacationDay = _dayAbsence?.type === 'vacation';
    const isMedicalDay = _dayAbsence?.type === 'medical';
    const isAbsenceDay = _dayAbsence?.type === 'absence';
    const schedule = getScheduleForDate(date);
    const isToday = new Date().toDateString() === date.toDateString();
    const isSelected = selectedDate?.toDateString() === date.toDateString();
    const dayOfWeek = date.getDay(); // 0=Dom, 6=Sáb
    // Día de semana seleccionado
    const jsDay = selectedWeekday === null ? -1 : (selectedWeekday === 6 ? 0 : selectedWeekday + 1);
    const isSelectedWeekday = weekdayMode && selectedWeekday !== null && date.getDay() === jsDay;
    // Rango
    const isRangeStart = rangeStart?.toDateString() === date.toDateString();
    const isRangeEnd = rangeEnd?.toDateString() === date.toDateString();
    const inRange = rangeStart && rangeEnd && (() => {
      const s = rangeStart < rangeEnd ? rangeStart : rangeEnd;
      const e = rangeStart < rangeEnd ? rangeEnd : rangeStart;
      return date >= s && date <= e;
    })();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const isSunday = dayOfWeek === 0;
    const isSaturday = dayOfWeek === 6;

    // Colores según estado
    let bgColor = 'white';
    let borderColor = '#DDD';
    let borderWidth = '1px';
    let textColor = '#333';

    if (isVacationDay) {
      bgColor = 'rgba(168,85,247,0.15)';
      borderColor = '#a855f7';
      borderWidth = '2px';
      textColor = '#7c3aed';
    } else if (isMedicalDay) {
      bgColor = 'rgba(239,68,68,0.1)';
      borderColor = '#ef4444';
      borderWidth = '2px';
      textColor = '#ef4444';
    } else if (isAbsenceDay) {
      bgColor = 'rgba(245,158,11,0.1)';
      borderColor = '#f59e0b';
      borderWidth = '2px';
      textColor = '#d97706';
    } else if (isToday) {
      bgColor = '#F97316';
      borderColor = '#EA580C';
      borderWidth = '3px';
      textColor = 'white';
    } else if (isSelectedWeekday) {
      bgColor = '#7C3AED';
      borderColor = '#6D28D9';
      borderWidth = '2px';
    } else if (isRangeStart) {
      bgColor = '#7C3AED';
      borderColor = '#6D28D9';
      borderWidth = '3px';
      textColor = 'white';
    } else if (isRangeEnd) {
      bgColor = '#2563EB';
      borderColor = '#1D4ED8';
      borderWidth = '3px';
      textColor = 'white';
    } else if (inRange) {
      bgColor = '#EDE9FE';
      borderColor = '#A78BFA';
      borderWidth = '2px';
    } else if (isSelected) {
      bgColor = '#DBEAFE';
      borderColor = '#2196F3';
      borderWidth = '3px';
    } else if (schedule) {
      bgColor = '#DCFCE7';
      borderColor = '#86EFAC';
    } else if (isSunday) {
      bgColor = '#FEE2E2'; // rojo suave
      borderColor = '#FCA5A5';
    } else if (isSaturday) {
      bgColor = '#F1F5F9'; // gris azulado
      borderColor = '#CBD5E1';
    }

    return (
      <button
        key={day}
        type="button"
        onClick={() => {
          if (rangeMode) {
            if (!rangeStart) {
              setRangeStart(date);
            } else {
              setRangeEnd(date);
            }
          } else {
            setSelectedDate(date);
            setEditingSchedule(schedule || {
              enabled: true,
              start: '09:00',
              end: '18:00',
              lunchStart: '13:00',
              lunchEnd: '14:00'
            });
          }
        }}
        style={{
          padding: '8px',
          border: `${borderWidth} solid ${borderColor}`,
          borderRadius: '8px',
          cursor: 'pointer',
          backgroundColor: bgColor,
          minHeight: '80px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          boxShadow: isToday ? '0 2px 8px rgba(249,115,22,0.5)' : 'none',
          WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
          textAlign: 'left',
          width: '100%',
          fontFamily: 'inherit',
        }}
      >
        <div style={{ 
          fontWeight: 'bold', 
          color: isToday ? 'white' : isSunday ? '#DC2626' : isSaturday ? '#64748B' : '#333',
          fontSize: isToday ? '16px' : '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          {day}
          {isToday && <span style={{fontSize:'10px', background:'rgba(255,255,255,0.3)', padding:'1px 5px', borderRadius:'4px', fontWeight:'700'}}>HOY</span>}
        </div>
        {_dayAbsence ? (
          <div style={{ fontSize: '11px', color: isVacationDay ? '#7c3aed' : isMedicalDay ? '#ef4444' : '#d97706', fontWeight:'700' }}>
            {isVacationDay ? '🏖️ Vacaciones' : isMedicalDay ? '🏥 Licencia' : '❌ Falta'}
          </div>
        ) : schedule ? (
          <>
            <div style={{ fontSize: '11px', color: isToday ? 'white' : '#16A34A', fontWeight:'600' }}>
              ✓ {schedule.start} - {schedule.end}
            </div>
            {schedule.lunchStart && (
              <div style={{ fontSize: '10px', color: isToday ? 'rgba(255,255,255,0.85)' : '#666' }}>
                🍽️ {schedule.lunchStart}-{schedule.lunchEnd}
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: '11px', color: isToday ? 'rgba(255,255,255,0.85)' : isWeekend ? '#94A3B8' : '#999' }}>
            {isWeekend ? '🏖️ Libre' : 'Libre'}
          </div>
        )}
      </button>
    );
  };

  const days = [];
  for (let i = 0; i < startDayOfWeek; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  return (
    <div className="monthly-calendar-overlay" onClick={onClose} style={{ position: "fixed", cursor: "default", WebkitTapHighlightColor: "transparent", top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999999, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", overflowY: "auto", padding: "20px" }}>
      <div 
        className="modal-content" 
        style={{ maxWidth: '1200px', maxHeight: '90vh', overflow: 'auto', position: 'relative', zIndex: 99999999, backgroundColor: 'var(--bg-card, #1e293b)', borderRadius: '12px', padding: '20px', color: 'var(--text-primary, #f1f5f9)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 style={{color:'var(--text-primary, #f1f5f9)', fontSize:'18px', fontWeight:'700'}}>📅 Calendario — {employee.name}</h2>
        </div>
        {employeeAbsences && employeeAbsences.length > 0 && (
          <div style={{background:'rgba(168,85,247,0.15)', border:'1px solid #a855f7', borderRadius:'8px', padding:'10px 14px', margin:'0 0 12px', display:'flex', flexWrap:'wrap', gap:'6px', alignItems:'center'}}>
            <span style={{fontSize:'13px', color:'#a855f7', fontWeight:'700'}}>📋 Ausencias registradas:</span>
            {employeeAbsences.map((a,i) => (
              <span key={i} style={{color:'#a855f7', fontSize:'12px', background:'rgba(168,85,247,0.1)', borderRadius:'6px', padding:'3px 8px'}}>
                {a.type === 'vacation' ? '🏖️ Vacaciones' : a.type === 'medical' ? '🏥 Licencia' : '❌ Falta'}: {a.startDate} → {a.endDate} ({a.days} días)
              </span>
            ))}
          </div>
        )}
        <div className="modal-header" style={{display:'none'}}>
          <button className="modal-close" onClick={onClose}>
            <span style={{ fontSize: '24px' }}>×</span>
          </button>
        </div>

        {/* Navegación del mes */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '20px',
          backgroundColor: '#F5F5F5',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <button
            onClick={() => setCurrentMonth(new Date(year, month - 1, 1))}
            style={{
              padding: '10px 20px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            ← Mes Anterior
          </button>
          <h3 style={{ margin: 0, color: '#2196F3', fontSize: '24px' }}>
            {monthNames[month]} {year}
          </h3>
          <button
            onClick={() => setCurrentMonth(new Date(year, month + 1, 1))}
            style={{
              padding: '10px 20px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Mes Siguiente →
          </button>
        </div>

        {/* Acciones rápidas */}
        <div style={{ 
          display: 'flex', 
          gap: '10px', 
          marginBottom: '20px',
          flexWrap: 'wrap' 
        }}>
          <button
            onClick={() => {
              if (selectedDate) {
                const weekStart = new Date(selectedDate);
                weekStart.setDate(weekStart.getDate() - ((weekStart.getDay() + 6) % 7));
                setCopiedWeek(copyWeekSchedule(weekStart));
                alert('✅ Semana copiada');
              }
            }}
            disabled={!selectedDate}
            style={{
              padding: '8px 16px',
              backgroundColor: selectedDate ? '#4CAF50' : '#CCC',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: selectedDate ? 'pointer' : 'not-allowed',
              fontWeight: 'bold'
            }}
          >
            📋 Copiar Semana
          </button>
          <button
            onClick={() => {
              if (selectedDate && copiedWeek) {
                const weekStart = new Date(selectedDate);
                weekStart.setDate(weekStart.getDate() - ((weekStart.getDay() + 6) % 7));
                pasteWeekSchedule(weekStart, copiedWeek);
                alert('✅ Semana pegada');
              }
            }}
            disabled={!selectedDate || !copiedWeek}
            style={{
              padding: '8px 16px',
              backgroundColor: (selectedDate && copiedWeek) ? '#FF9800' : '#CCC',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: (selectedDate && copiedWeek) ? 'pointer' : 'not-allowed',
              fontWeight: 'bold'
            }}
          >
            📌 Pegar Semana
          </button>
          <button
            onClick={() => {
              setWeekdayMode(!weekdayMode);
              setSelectedWeekday(null);
              setRangeMode(false);
              setSelectedDate(null);
              setEditingSchedule(null);
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: weekdayMode ? '#7C3AED' : '#64748B',
              color: 'white',
              border: weekdayMode ? '2px solid #A78BFA' : 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            {weekdayMode ? '✕ Cancelar' : '📆 Por Día'}
          </button>
          <button
            onClick={() => {
              setRangeMode(!rangeMode);
              setRangeStart(null);
              setRangeEnd(null);
              setWeekdayMode(false);
              setSelectedDate(null);
              setEditingSchedule(null);
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: rangeMode ? '#7C3AED' : '#64748B',
              color: 'white',
              border: rangeMode ? '2px solid #A78BFA' : 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            {rangeMode ? '✕ Cancelar Rango' : '📅 Modo Rango'}
          </button>
          {rangeMode && rangeStart && rangeEnd && (
            <button
              onClick={applyRange}
              style={{
                padding: '8px 16px',
                backgroundColor: '#22c55e',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
              }}
            >
              ✓ Aplicar Rango
            </button>
          )}
          <button
            onClick={handleSave}
            style={{
              padding: '8px 16px',
              backgroundColor: '#9C27B0',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold',
              marginLeft: 'auto'
            }}
          >
            💾 Guardar Todo
          </button>
        </div>

        {/* Calendario */}
        <div style={{overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: '20px'}}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(90px, 1fr))',
          gap: '6px',
          minWidth: '630px',
        }}>
          {dayNames.map((day, idx) => (
            <div key={day} style={{
              padding: '10px',
              fontWeight: 'bold',
              textAlign: 'center',
              backgroundColor: idx === 6 ? '#EF4444' : idx === 5 ? '#64748B' : '#2196F3',
              color: 'white',
              borderRadius: '6px',
              fontSize: '14px',
            }}>
              {day}
            </div>
          ))}
          {days.map((day, index) => renderDay(day))}
        </div>
        </div>

        {/* Selector de día de semana */}
        {weekdayMode && (
          <div style={{background:'rgba(124,58,237,0.1)', border:'1px solid rgba(124,58,237,0.3)', borderRadius:'12px', padding:'16px', marginBottom:'12px'}}>
            <p style={{margin:'0 0 12px', fontSize:'13px', color:'#A78BFA', fontWeight:'700'}}>
              Selecciona el día de la semana a configurar:
            </p>
            <div style={{display:'flex', gap:'8px', flexWrap:'wrap', marginBottom:'14px'}}>
              {dayNames.map((name, idx) => (
                <button key={idx} type="button"
                  onClick={() => {
                    setSelectedWeekday(idx);
                    setWeekdaySchedule({ enabled: true, start: '09:00', end: '18:00', lunchStart: '13:00', lunchEnd: '14:00' });
                  }}
                  style={{padding:'8px 14px', background: selectedWeekday===idx ? '#7C3AED' : 'var(--bg-elevated,#1e2840)', color: selectedWeekday===idx ? 'white' : '#A78BFA', border:`2px solid ${selectedWeekday===idx?'#7C3AED':'rgba(124,58,237,0.3)'}`, borderRadius:'8px', cursor:'pointer', fontWeight:'700', fontSize:'13px', fontFamily:'inherit'}}>
                  {name}
                </button>
              ))}
            </div>
            {selectedWeekday !== null && (
              <div style={{background:'var(--bg-elevated,#1e2840)', borderRadius:'10px', padding:'14px'}}>
                <p style={{margin:'0 0 10px', fontSize:'13px', fontWeight:'700', color:'#f1f5f9'}}>
                  Horario para todos los {dayNames[selectedWeekday]}:
                </p>
                <div style={{display:'flex', alignItems:'center', gap:'10px', marginBottom:'12px'}}>
                  <label style={{display:'flex', alignItems:'center', gap:'6px', color:'#f1f5f9', fontSize:'13px'}}>
                    <input type="checkbox" checked={weekdaySchedule.enabled}
                      onChange={e => setWeekdaySchedule({...weekdaySchedule, enabled: e.target.checked})}
                      style={{width:'18px', height:'18px'}} />
                    Trabaja este día
                  </label>
                </div>
                {weekdaySchedule.enabled && (
                  <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(130px, 1fr))', gap:'10px', marginBottom:'14px'}}>
                    {[
                      {label:'Entrada', key:'start'},
                      {label:'Salida', key:'end'},
                      {label:'Inicio Colación', key:'lunchStart'},
                      {label:'Fin Colación', key:'lunchEnd'},
                    ].map(({label, key}) => (
                      <div key={key}>
                        <label style={{display:'block', marginBottom:'4px', fontSize:'12px', fontWeight:'600', color:'#94a3b8'}}>{label}</label>
                        <input type="time" value={weekdaySchedule[key]||''}
                          onChange={e => setWeekdaySchedule({...weekdaySchedule, [key]: e.target.value})}
                          style={{width:'100%', padding:'8px', borderRadius:'6px', border:'1px solid #475569', fontSize:'15px', background:'var(--bg-card,#161d2e)', color:'var(--text-primary,#f1f5f9)'}} />
                      </div>
                    ))}
                  </div>
                )}
                <button type="button" onClick={applyWeekday}
                  style={{width:'100%', padding:'12px', background:'#22c55e', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'700', fontSize:'14px', fontFamily:'inherit'}}>
                  ✓ Aplicar a todos los {dayNames[selectedWeekday]}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Instrucciones modo rango */}
        {rangeMode && (
          <div style={{background:'rgba(124,58,237,0.15)', border:'1px solid rgba(124,58,237,0.4)', borderRadius:'10px', padding:'12px 16px', marginBottom:'12px', fontSize:'13px', color:'#A78BFA'}}>
            {!rangeStart ? '🟣 Toca el día ORIGEN (con horario configurado)' :
             !rangeEnd ? `✅ Origen: día ${rangeStart.getDate()} — Ahora toca el día DESTINO` :
             `🟣 Día ${rangeStart.getDate()} → 🔵 Día ${rangeEnd.getDate()} — Presiona "✓ Aplicar Rango"`}
          </div>
        )}

        {/* Editor de horario del día seleccionado */}
        {selectedDate && editingSchedule && (
          <div style={{
            padding: '20px',
            backgroundColor: '#1e3a5f',
            borderRadius: '12px',
            border: '2px solid #2196F3',
            marginTop: '12px',
          }}>
            <h3 style={{ marginTop: 0, color: '#60a5fa' }}>
              Editar: {selectedDate.toLocaleDateString('es-ES', { 
                weekday: 'long', 
                day: 'numeric', 
                month: 'long' 
              })}
            </h3>

            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={editingSchedule.enabled}
                  onChange={(e) => setEditingSchedule({
                    ...editingSchedule,
                    enabled: e.target.checked
                  })}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
                <strong>Trabaja este día</strong>
              </label>
            </div>

            {editingSchedule.enabled && (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '15px',
                marginBottom: '15px'
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#f1f5f9' }}>
                    Entrada:
                  </label>
                  <input
                    type="time"
                    value={editingSchedule.start || ''}
                    onChange={(e) => setEditingSchedule({
                      ...editingSchedule,
                      start: e.target.value
                    })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #DDD',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#f1f5f9' }}>
                    Salida:
                  </label>
                  <input
                    type="time"
                    value={editingSchedule.end || ''}
                    onChange={(e) => setEditingSchedule({
                      ...editingSchedule,
                      end: e.target.value
                    })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #DDD',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#f1f5f9' }}>
                    Inicio Colación:
                  </label>
                  <input
                    type="time"
                    value={editingSchedule.lunchStart || ''}
                    onChange={(e) => setEditingSchedule({
                      ...editingSchedule,
                      lunchStart: e.target.value
                    })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #DDD',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#f1f5f9' }}>
                    Fin Colación:
                  </label>
                  <input
                    type="time"
                    value={editingSchedule.lunchEnd || ''}
                    onChange={(e) => setEditingSchedule({
                      ...editingSchedule,
                      lunchEnd: e.target.value
                    })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #DDD',
                      fontSize: '16px'
                    }}
                  />
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setScheduleForDate(
                    selectedDate, 
                    editingSchedule.enabled ? editingSchedule : null
                  );
                  // Toast feedback al aplicar día
                  const t = document.createElement('div');
                  t.textContent = '✓ Día actualizado — presiona Guardar Todo para confirmar';
                  t.style.cssText = 'position:fixed;bottom:100px;left:50%;transform:translateX(-50%);background:#2196F3;color:white;padding:10px 20px;border-radius:10px;font-weight:600;font-size:13px;z-index:9999999;box-shadow:0 4px 12px rgba(0,0,0,0.3);font-family:sans-serif;text-align:center;max-width:90vw;';
                  document.body.appendChild(t);
                  setTimeout(() => t.remove(), 2500);
                  setSelectedDate(null);
                  setEditingSchedule(null);
                }}
                style={{
                  padding: '14px 28px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '16px',
                }}
              >
                ✓ Aplicar
              </button>
              <button
                onClick={() => {
                  setScheduleForDate(selectedDate, null);
                  setSelectedDate(null);
                  setEditingSchedule(null);
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#F44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                🗑️ Marcar Libre
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyCalendar;
